/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/packages/enterprise-admin/src/utils/passive-event-fix.ts
 * 版本: v1.0
 * 创建日期: 2025-12-20
 * 创建人: AI Assistant
 *
 * Passive Event Listener Polyfill
 *
 * 功能：
 * - 解决 Element Plus 组件（ElTable, ElScrollbar）触发的 Chrome Violation 警告
 * - 将 wheel/touchstart/touchmove 事件默认设为 passive（当未明确指定时）
 *
 * 注意：
 * - 此 polyfill 仅对未明确指定 passive 选项的监听器生效
 * - 如果组件需要调用 preventDefault()，应明确传入 { passive: false }
 */

export function installPassiveEventFix(): void {
  if (typeof window === 'undefined' || typeof EventTarget === 'undefined') {
    return
  }

  // 检查浏览器是否支持 passive 选项
  let supportsPassive = false
  try {
    const opts = Object.defineProperty({}, 'passive', {
      get() {
        supportsPassive = true
        return true
      }
    }) as AddEventListenerOptions
    const noop = () => {}
    window.addEventListener('testPassive', noop, opts)
    window.removeEventListener('testPassive', noop, opts)
  } catch {
    supportsPassive = false
  }

  if (!supportsPassive) {
    return
  }

  // 需要设为 passive 的事件类型
  const passiveEvents = ['wheel', 'mousewheel', 'touchstart', 'touchmove']

  const originalAddEventListener = EventTarget.prototype.addEventListener

  EventTarget.prototype.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ): void {
    let modifiedOptions = options

    if (passiveEvents.includes(type)) {
      if (options === undefined || options === false) {
        // 未指定选项或仅指定 capture: false，默认设为 passive
        modifiedOptions = { capture: false, passive: true }
      } else if (options === true) {
        // 仅指定 capture: true，添加 passive
        modifiedOptions = { capture: true, passive: true }
      } else if (typeof options === 'object') {
        // 对象选项：仅当未明确指定 passive 时才设为 true
        if (options.passive === undefined) {
          modifiedOptions = { ...options, passive: true }
        }
        // 如果已明确指定 passive: false，保持不变（组件可能需要 preventDefault）
      }
    }

    return originalAddEventListener.call(this, type, listener, modifiedOptions)
  }

  // 开发环境下输出提示
  if (import.meta?.env?.DEV) {
    console.info('[PassiveEventFix] Installed passive event listener polyfill')
  }
}

